// Lib
import React from 'react';

// Components
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';

// Types
import type {
  OrganizationAnswers,
  OrganizationErrors,
} from '../../model/useCreateOrganization';

interface GeoItem {
  name: string;
}

interface ShippingStepProps {
  answers: OrganizationAnswers;
  error: OrganizationErrors;
  onChange: (name: string, value: string) => void;
  onSetError: (name: string, value: string) => void;
  countries: GeoItem[];
  states: GeoItem[];
  cities: GeoItem[];
}

interface GeoSelectProps {
  placeholder: string;
  value?: string;
  disabled?: boolean;
  items: GeoItem[];
  onSelect: (value: string) => void;
}

const GeoSelect: React.FC<GeoSelectProps> = ({
  placeholder,
  value,
  disabled,
  items,
  onSelect,
}) => (
  <Select
    value={value || undefined}
    disabled={disabled}
    onValueChange={onSelect}
  >
    <SelectTrigger className="w-full cursor-pointer disabled:cursor-not-allowed">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {items?.map(({ name }) => (
        <SelectItem key={name} value={name} className="cursor-pointer">
          {name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const ShippingStep: React.FC<ShippingStepProps> = ({
  answers,
  error,
  onChange,
  onSetError,
  countries,
  states,
  cities,
}) => (
  <>
    <h1 className="mb-4 text-center text-2xl font-bold tracking-tight text-foreground">
      Add your shipping info
    </h1>
    <p className="mb-6 text-center text-sm text-foreground">
      So brands can send you their products or gifts
    </p>
    <div className="grid grid-cols-2 gap-x-2 gap-y-3">
      <GeoSelect
        placeholder="Country"
        value={answers?.country}
        items={countries}
        onSelect={(name) => {
          if (error?.country) onSetError('country', '');
          onChange('country', name);
          onChange('state', '');
          onChange('city', '');
        }}
      />
      <GeoSelect
        placeholder="State / Region"
        value={answers?.state}
        disabled={!answers?.country}
        items={states}
        onSelect={(name) => {
          if (error?.state) onSetError('state', '');
          onChange('state', name);
          onChange('city', '');
        }}
      />
      <GeoSelect
        placeholder="City"
        value={answers?.city}
        disabled={!answers?.state}
        items={cities}
        onSelect={(name) => {
          if (error?.city) onSetError('city', '');
          onChange('city', name);
        }}
      />
      <Input
        type="text"
        name="postalCode"
        placeholder="Postal code"
        aria-invalid={Boolean(error?.postalCode)}
        onChange={(field) => {
          if (error?.postalCode) onSetError('postalCode', '');
          onChange('postalCode', field.target.value);
        }}
        value={answers?.postalCode}
      />
      <Input
        type="text"
        name="streetAddress"
        className="col-span-2"
        placeholder="Street address"
        aria-invalid={Boolean(error?.streetAddress)}
        onChange={(field) => {
          if (error?.streetAddress) onSetError('streetAddress', '');
          onChange('streetAddress', field.target.value);
        }}
        value={answers?.streetAddress}
      />
      <Input
        type="text"
        name="fullName"
        className="col-span-2"
        placeholder="Full name"
        aria-invalid={Boolean(error?.fullName)}
        onChange={(field) => {
          if (error?.fullName) onSetError('fullName', '');
          onChange('fullName', field.target.value);
        }}
        value={answers?.fullName}
      />
    </div>
  </>
);
